import { ErrorLike } from "@apollo/client";
import { defaultTestId, HasIdAndName, StepperLayout, StepperLayoutStep, useComputed } from "@homebound/beam";
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { createMaterialCatalogUrl, createMaterialDetailsUrl, createMaterialPageUrl } from "src/RouteUrls";
import { Loading } from "src/components/Loading";
import { type AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { useUserFeatureFlagsQuery } from "src/contexts/FeatureFlags/FeatureFlagContext.generated";
import type { NamedFragment } from "src/fragments/commonFragments.generated";
import { AiProposalStatus, FeatureFlagType } from "src/generated/graphqlTypes";
import {
  MaterialCatalog_ItemFragment,
  MaterialCatalog_MaterialBrandFragment,
  useMaterialCatalogMetadataQuery,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import {
  acceptRemainingProposals,
  importedListingId,
  listingFieldProposals,
  VariantFieldProposals,
  variantFieldProposals,
} from "src/routes/libraries/material-catalog/components/materialListingAiProposals";
import type { MaterialListing_DetailFragment } from "src/routes/libraries/material-catalog/components/materialListingDetail.generated";
import {
  MaterialImportAiProposalQuery,
  useCreateMaterialImportAiProposalMutation,
  useMaterialImportAiProposalQuery,
} from "src/routes/libraries/material-catalog/material-import/MaterialImportPage.generated";
import { MaterialImportReviewStep } from "src/routes/libraries/material-catalog/material-import/components/MaterialImportReviewStep";
import {
  MaterialImportState,
  MaterialImportStep,
} from "src/routes/libraries/material-catalog/material-import/components/MaterialImportStep";
import { materialImportFormConfig } from "src/routes/libraries/material-catalog/material-import/materialImportForm";
import {
  MaterialListingByListingIdProvider,
  useMaterialListingContext,
} from "src/routes/libraries/material-catalog/material-listing-page/components/material-listing-context/MaterialListingContext";
import { queryResult } from "src/utils";
import { useFormState } from "src/utils/formState";

const detailsStepLabel = "Details";

/**
 * The AI material import wizard: hand the agent a product page URL, then review what it found.
 *
 * Gated on `MATERIAL_IMPORT_AI`; without the flag, adding a material is still the hand-entry page.
 */
export function MaterialImportPage() {
  const isMaterialImportAiEnabled = useFeatureFlag(FeatureFlagType.MaterialImportAi);
  const { loading } = useUserFeatureFlagsQuery();

  if (loading) return <Loading />;
  if (!isMaterialImportAiEnabled) return <Navigate to={createMaterialPageUrl()} replace />;
  return <MaterialImportPageMetadata />;
}

function MaterialImportPageMetadata() {
  const query = useMaterialCatalogMetadataQuery();
  const { refetch } = query;
  // The agent can add to these lists mid-import, so the post-import view re-reads them on mount.
  // (i.e. it auto-creates a brand or a MAV we didn't have on initial page load)
  const refetchMetadata = useCallback(() => void refetch().catch(() => undefined), [refetch]);
  return queryResult(query, ({ items, materialBrands, materialVariantTags, markets }) => (
    <MaterialImportPageView
      items={items}
      brands={materialBrands}
      tags={materialVariantTags}
      markets={markets}
      refetchMetadata={refetchMetadata}
    />
  ));
}

type MaterialImportPageViewProps = {
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
  tags: NamedFragment[];
  markets: HasIdAndName[];
  refetchMetadata: VoidFunction;
};

function MaterialImportPageView(props: MaterialImportPageViewProps) {
  const { items, brands, tags, markets, refetchMetadata } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const proposalId = searchParams.get("proposal") ?? undefined;

  const [createProposal] = useCreateMaterialImportAiProposalMutation();
  const { data, error, startPolling, stopPolling } = useMaterialImportAiProposalQuery({
    variables: { id: proposalId! },
    skip: !proposalId,
  });
  const proposal = proposalId ? data?.materialImportAiProposal : undefined;
  // Both are gated on `proposalId` so Try Again's param clear can't leave a stale proposal or error behind.
  const proposalError = proposalId ? error : undefined;
  const listingId = importedListingId(proposal);
  const importState = toImportState(proposalId, proposal, listingId, proposalError);

  importState === "processing" ? startPolling(10_000) : stopPolling();

  const formState = useFormState({ config: materialImportFormConfig });
  const canSubmitImport = useComputed(() => formState.valid, [formState]);

  const onSubmitImport = useCallback(async () => {
    const { itemId, manufacturerUrl } = formState.value;
    // Continue is gated on the form's validity, so this only guards the types.
    if (!itemId || !manufacturerUrl) return false;
    // TODO: Add some url validation
    const result = await createProposal({ variables: { input: { itemId, manufacturerUrl } } }).catch(() => undefined);
    const id = result?.data?.createMaterialImportAiProposal.id;
    if (!id) return false;
    setSearchParams({ proposal: id }, { replace: true });
    return false;
  }, [createProposal, formState, setSearchParams]);

  const onResetImport = useCallback(() => {
    // Carry the failed attempt back into the form so the user can tweak the URL rather than retype it.
    // An unreadable proposal has nothing to carry, so this just drops the param.
    if (proposal) formState.set({ itemId: proposal.item.id, manufacturerUrl: proposal.manufacturerUrl });
    setSearchParams({}, { replace: true });
  }, [proposal, formState, setSearchParams]);

  // Deleting the import's last variant erases the listing server-side, so there is nothing left to
  // review — drop back to step one with the url still filled in, the same place 'Try Again' lands.
  // Clearing `?proposal=` looks like a navigation to `useBlocker`, so record the discard first:
  // otherwise the review step's unsaved-changes prompt fires on the way out of an import the user
  // deliberately threw away.
  const discardedListingId = useRef<string | undefined>(undefined);
  const onImportDiscarded = useCallback(() => {
    discardedListingId.current = listingId;
    onResetImport();
  }, [listingId, onResetImport]);

  const canSubmitFromImportStep = importState === "idle" || importState === "failed" || importState === "completed";

  const importStep: StepperLayoutStep = {
    label: "Import",
    // Continue stays disabled while the agent is running; once it lands it just moves along.
    primaryDisabled: !(canSubmitFromImportStep ? canSubmitImport : importState === "done"),
    onContinue: canSubmitFromImportStep ? onSubmitImport : undefined,
    content: (
      <MaterialImportStep
        formState={formState}
        items={items}
        state={importState}
        failureReason={failureReason(proposal, listingId, proposalError)}
        submitted={proposal && { itemName: proposal.item.name, manufacturerUrl: proposal.manufacturerUrl }}
        progressText={proposal?.progressText ?? undefined}
        failureAction={{
          label: importState === "completed" ? "Start Another" : proposal ? "Try Again" : "Dismiss",
          onClick: onResetImport,
        }}
      />
    ),
  };

  // No listing yet means we are pre-import
  if (!listingId) return <PreImportWizard importStep={importStep} />;

  return (
    <PostImportWizard
      listingId={listingId}
      importStep={importStep}
      items={items}
      brands={brands}
      tags={tags}
      markets={markets}
      listingFieldProposals={listingFieldProposals(proposal)}
      variantFieldProposals={variantFieldProposals(proposal)}
      refetchMetadata={refetchMetadata}
      onListingDeleted={onImportDiscarded}
      isDiscarded={() => discardedListingId.current === listingId}
    />
  );
}

type PreImportWizardProps = { importStep: StepperLayoutStep };

/**
 * The wizard before an import has produced anything: one real step, and a Details step that is only
 * there so the reviewer can see what is coming.
 */
function PreImportWizard(props: PreImportWizardProps) {
  const { importStep } = props;
  const navigate = useNavigate();

  return (
    <StepperLayout
      key="import"
      aiMode
      title="Add Material"
      breadcrumbs={{ breadcrumbs: { label: "Material Library", href: createMaterialCatalogUrl() } }}
      steps={[importStep, { label: detailsStepLabel, disabled: true, primaryDisabled: true, content: null }]}
      onCancel={() => navigate(createMaterialCatalogUrl())}
      completeLabel="Create"
      onComplete={() => {}}
    />
  );
}

type PostImportWizardProps = {
  listingId: string;
  importStep: StepperLayoutStep;
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
  tags: NamedFragment[];
  markets: HasIdAndName[];
  /** The agent's proposals for the listing's own fields, so the review step can highlight them. */
  listingFieldProposals: AiFieldProposalFormValue[];
  /** The same, for each imported variant's own fields, keyed by variant id. */
  variantFieldProposals: VariantFieldProposals;
  /** Re-reads the option lists, which the agent may have added brands and MAVs to. */
  refetchMetadata: VoidFunction;
  /** The reviewer deleted the import's last variant, so the listing went with it. */
  onListingDeleted: VoidFunction;
  /** True once *this* import was deleted, so its unsaved edits are no longer worth prompting about. */
  isDiscarded: () => boolean;
};

/**
 * Mounts the listing form for the ai import's listing, and owns which variant the review step is on.
 *
 * Only sets up the provider; {@link PostImportStepper} renders the wizard itself.
 */
function PostImportWizard(props: PostImportWizardProps) {
  const { listingId, listingFieldProposals, variantFieldProposals } = props;
  const { items, brands, tags, markets, importStep, refetchMetadata, onListingDeleted, isDiscarded } = props;
  const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(undefined);
  const [createNew, setCreateNew] = useState(false);

  const onSelectVariant = useCallback((id: string | undefined) => {
    setCreateNew(false);
    setSelectedVariantId(id);
  }, []);

  const onCreateNew = useCallback(() => setCreateNew(true), []);

  const onListingLoaded = useCallback((listing: MaterialListing_DetailFragment) => {
    // Land on the default variant, the same one the detail page would have picked.
    setSelectedVariantId((current) => current ?? listing.defaultVariant?.id ?? listing.materialVariants.first?.id);
  }, []);

  // The agent creates whatever MAVs and brand it needs so we need to make sure we have all the possible options on mount
  useEffect(() => refetchMetadata(), [refetchMetadata]);

  return (
    <MaterialListingByListingIdProvider
      listingId={listingId}
      listingFieldProposals={listingFieldProposals}
      variantFieldProposals={variantFieldProposals}
      selectedVariantId={selectedVariantId}
      createNew={createNew}
      onSelectVariant={onSelectVariant}
      onCreateNew={onCreateNew}
      onListingDeleted={onListingDeleted}
      onListingLoaded={onListingLoaded}
    >
      <PostImportStepper
        listingId={listingId}
        importStep={importStep}
        items={items}
        brands={brands}
        tags={tags}
        markets={markets}
        isDiscarded={isDiscarded}
      />
    </MaterialListingByListingIdProvider>
  );
}

type PostImportStepperProps = {
  listingId: string;
  importStep: StepperLayoutStep;
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
  tags: NamedFragment[];
  markets: HasIdAndName[];
  isDiscarded: () => boolean;
};

/** The wizard once the listing form is mounted, opened on the Details step the import filled in. */
function PostImportStepper(props: PostImportStepperProps) {
  const { listingId, importStep, items, brands, tags, markets, isDiscarded } = props;
  const navigate = useNavigate();
  const { formState, save } = useMaterialListingContext();
  const canCreate = useComputed(() => formState.valid, [formState]);
  // Set once the save lands, so the hand-off below isn't treated as abandoning unsaved work.
  const saved = useRef(false);

  const onComplete = useCallback(async () => {
    // Touch the fields so any validation error is visible, the way `SubmitButton` does on the detail page.
    if (!formState.canSave()) return;
    // Applies each remaining proposal's value and resolves its status; an UPDATE proposal holds the
    // only copy of the agent's value, so the two have to happen together.
    acceptRemainingProposals(formState, { items, brands });
    if (!(await save())) return;
    saved.current = true;
    // `replace` so Back doesn't return to a `?proposal=` that has just been resolved.
    navigate(createMaterialDetailsUrl(listingId), { replace: true });
  }, [formState, save, navigate, listingId, items, brands]);

  return (
    <StepperLayout
      key={listingId}
      aiMode
      title="Add Material"
      breadcrumbs={{ breadcrumbs: { label: "Material Library", href: createMaterialCatalogUrl() } }}
      steps={[
        importStep,
        {
          label: detailsStepLabel,
          primaryDisabled: !canCreate,
          content: <MaterialImportReviewStep items={items} brands={brands} tags={tags} markets={markets} />,
        },
      ]}
      defaultStep={defaultTestId(detailsStepLabel)}
      onCancel={() => navigate(createMaterialCatalogUrl())}
      completeLabel="Create"
      onComplete={onComplete}
      // Always prompt on Cancel after import even when the form is not dirty.
      // `saved` / `isDiscarded` clear the guard so Create hand-off and deleting
      // the last variant are not treated as abandoning work.
      isDirty={() => !saved.current && !isDiscarded()}
    />
  );
}

/** Which body the wizard shows. */
function toImportState(
  proposalId: string | undefined,
  proposal: MaterialImportAiProposalQuery["materialImportAiProposal"] | undefined,
  listingId: string | undefined,
  error: ErrorLike | undefined,
): MaterialImportState {
  if (!proposalId) return "idle";
  if (error) return "failed";
  // Cold load of `?proposal=`: we don't know the status yet, and a running agent is the likely answer.
  if (!proposal) return "processing";
  if (proposal.status.code === AiProposalStatus.Pending) return "processing";
  // Fully resolved: the entity proposals are soft-deleted, so there is no listing left to point at.
  // Checked before `listingId` so a spent `?proposal=` doesn't read as "we found nothing".
  if (proposal.deletedAt) return "completed";
  return listingId ? "done" : "failed";
}

function failureReason(
  proposal: MaterialImportAiProposalQuery["materialImportAiProposal"] | undefined,
  listingId: string | undefined,
  error: ErrorLike | undefined,
): string | undefined {
  if (error) return error.message;
  if (!proposal || listingId || proposal.deletedAt) return undefined;
  if (proposal.failureReason) return proposal.failureReason;
  if (proposal.status.code === AiProposalStatus.Pending) return undefined;
  // The agent didn't find a material on the page, so the user needs to enter the details manually.
  return "We couldn't find a material on that page. Check the URL, or enter the details manually.";
}
